import { Card, cn, PageHeader } from '@fidalia/ui';
import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import {
  MdBusiness,
  MdDraw,
  MdLockOutline,
  MdPerson,
  MdSend,
  MdSettings,
  MdStar,
} from 'react-icons/md';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsLayout,
});

/** Le sommaire : deux rubriques, et une page (une adresse) par réglage. */
const GROUPES = [
  {
    titre: 'Compte',
    pages: [
      { to: '/settings/account', label: 'Mon compte', icon: <MdPerson /> },
      { to: '/settings/practice', label: 'Mon cabinet', icon: <MdBusiness /> },
    ],
  },
  {
    titre: 'E-mails',
    pages: [
      { to: '/settings/sending', label: 'Adresse d’expédition', icon: <MdSend /> },
      { to: '/settings/signatures', label: 'Signature', icon: <MdDraw /> },
    ],
  },
  {
    titre: 'Messages',
    pages: [{ to: '/settings/reviews', label: 'Avis Google', icon: <MdStar /> }],
  },
] as const;

/**
 * Réglages du cabinet : le sommaire à gauche, la page ouverte à droite.
 *
 * Chaque réglage a sa propre adresse (`/settings/sending`…) : on peut y renvoyer
 * depuis un message, un courriel ou l'extension, et le bouton Retour du
 * navigateur fait ce qu'on attend. Adresse d'envoi et signature restent deux
 * pages : l'une tient à un serveur, l'autre à ce qu'on écrit.
 */
function SettingsLayout() {
  const { me } = Route.useRouteContext();

  // Les réglages gouvernent le cabinet : réservés à ses administrateurs. Le lien est
  // masqué pour les comptes secondaires ; ouvert par une adresse directe, l'écran
  // dit pourquoi il n'y a rien à voir plutôt que de renvoyer ailleurs sans un mot.
  if (me.user.role !== 'admin') return <NonAutorise />;

  return (
    // `min-h-0 flex-1` : l'écran prend la hauteur que lui laisse la coquille et
    // ne la dépasse pas — c'est le panneau de droite qui défile, pas la page.
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <PageHeader
        icon={<MdSettings />}
        title="Réglages"
        description="Votre compte, votre cabinet, et l’adresse qui envoie vos emails."
      />

      <div className="flex min-h-0 flex-1 flex-col gap-6 lg:flex-row lg:gap-8">
        <nav
          aria-label="Réglages"
          className="flex flex-col gap-1 rounded-card border border-border-subtle bg-surface p-2 shadow-card lg:w-60 lg:shrink-0"
        >
          {GROUPES.map((groupe, index) => (
            <div key={groupe.titre} className="flex flex-col gap-1">
              <p
                className={cn(
                  'px-3 pb-1 text-xs font-semibold tracking-wide text-content-subtle uppercase',
                  index === 0 ? 'pt-2' : 'pt-3',
                )}
              >
                {groupe.titre}
              </p>
              {groupe.pages.map((page) => (
                <Link
                  key={page.to}
                  to={page.to}
                  className="flex w-full items-center gap-2 rounded-field px-3 py-2.5 text-left text-sm font-medium text-content-muted transition-colors hover:bg-surface-muted hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/30"
                  activeProps={{
                    className: '!bg-brand-50 !font-semibold !text-brand-600',
                    'aria-current': 'page',
                  }}
                >
                  <span aria-hidden="true" className="flex [&>svg]:size-5">
                    {page.icon}
                  </span>
                  {page.label}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        {/* Le contenu tient dans une seule carte, qui défile par l'intérieur :
            l'en-tête de page et le sommaire restent en place. */}
        <Card className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
          <Outlet />
        </Card>
      </div>
    </div>
  );
}

/** Écran des comptes secondaires qui arrivent sur les réglages par une adresse directe. */
function NonAutorise() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <Card className="flex max-w-md flex-col items-center gap-3 p-8 text-center">
        <span aria-hidden="true" className="text-4xl text-content-subtle">
          <MdLockOutline />
        </span>
        <h1 className="text-lg font-semibold text-content">Vous n’êtes pas autorisé</h1>
        <p className="text-sm text-content-muted">
          Vous n’avez pas accès aux réglages du cabinet. Si vous avez besoin de modifier ces
          informations, demandez à votre administrateur.
        </p>
      </Card>
    </div>
  );
}
